import { DateTime } from "luxon";
import type { CustomTimeGroup, ScheduleRow } from "../types";
import { formatTimeTo12Hour } from "../util/shiftTimeFormat";

export type { CustomTimeGroup } from "../types";

export const MAX_CUSTOM_TIME_GROUPS = 12;
const MINUTES_PER_DAY = 24 * 60;
const MILLIS_PER_MINUTE = 60 * 1000;
const MINIMUM_COVERED_MINUTES = 60;
const ADJACENT_DAY_OFFSETS = [-1, 0, 1] as const;
const CLOCK_ANCHOR = { year: 2000, month: 1, day: 1 } as const;
const CONFIGURED_GROUP_KEY_PREFIX = "custom-time-group:configured:";
const UNMATCHED_GROUP_KEY = "custom-time-group:unmatched";

export interface CustomTimeGroupBand {
	key: string;
	label: string;
	rows: ScheduleRow[];
}

interface BuildCustomTimeGroupBandsArgs {
	rows: ScheduleRow[];
	groups: CustomTimeGroup[];
	timezone: string;
	dayStart: string;
	sortDirection: "asc" | "desc";
	otherLabel: string;
}

const minuteToTime = (minute: number): DateTime =>
	DateTime.fromObject({ ...CLOCK_ANCHOR, hour: Math.floor(minute / 60), minute: minute % 60 }, { zone: "UTC" });

export const formatCustomTimeGroupRange = (group: Pick<CustomTimeGroup, "startMinute" | "endMinute">): string =>
	`${formatTimeTo12Hour(minuteToTime(group.startMinute), undefined)} - ${formatTimeTo12Hour(minuteToTime(group.endMinute), undefined)}`;

const parseDayStartMinute = (dayStart: string): number => {
	const [hourText, minuteText] = dayStart.split(":");
	const hour = Number(hourText);
	const minute = Number(minuteText);
	if (!Number.isInteger(hour) || !Number.isInteger(minute) || hour < 0 || hour > 23 || minute < 0 || minute > 59) {
		throw new Error(`Invalid department day start: ${dayStart}`);
	}
	return hour * 60 + minute;
};

const groupDurationMinutes = (group: CustomTimeGroup): number => {
	const duration = (group.endMinute - group.startMinute + MINUTES_PER_DAY) % MINUTES_PER_DAY;
	// A zero-length group would need zero covered minutes and silently match every shift.
	if (duration === 0) throw new Error(`Custom time group ${group.id} has the same start and end time`);
	return duration;
};

/** The group's clock range on one calendar day; a range ending at or before its start ends the next morning. */
const groupIntervalOnDay = (group: CustomTimeGroup, day: DateTime): { start: DateTime; end: DateTime } => {
	// Both ends are wall-clock times, so a DST night does not move the group's end by an hour.
	const start = day.set({ hour: Math.floor(group.startMinute / 60), minute: group.startMinute % 60 });
	const end = day.set({ hour: Math.floor(group.endMinute / 60), minute: group.endMinute % 60 });
	return { start, end: group.endMinute > group.startMinute ? end : end.plus({ days: 1 }) };
};

const overlapMinutes = (left: { start: DateTime; end: DateTime }, right: { start: DateTime; end: DateTime }): number => {
	const overlapMillis = Math.min(left.end.toMillis(), right.end.toMillis()) - Math.max(left.start.toMillis(), right.start.toMillis());
	return Math.max(0, overlapMillis) / MILLIS_PER_MINUTE;
};

/**
 * A shift belongs to a group when it covers at least an hour of it (all of it, for a group shorter
 * than an hour). The group is tried on the shift's own calendar day and the days either side, so
 * overnight shifts and groups match on wall-clock time without depending on the department day start.
 */
const rowCoversGroup = (row: ScheduleRow, group: CustomTimeGroup, timezone: string): boolean => {
	const shift = { start: row.shiftStart.setZone(timezone), end: row.shiftEnd.setZone(timezone) };
	const requiredMinutes = Math.min(MINIMUM_COVERED_MINUTES, groupDurationMinutes(group));
	const shiftDay = shift.start.startOf("day");
	return ADJACENT_DAY_OFFSETS.some((offset) => overlapMinutes(shift, groupIntervalOnDay(group, shiftDay.plus({ days: offset }))) >= requiredMinutes);
};

const groupTimelineMinute = (group: CustomTimeGroup, dayStartMinute: number): number =>
	(group.startMinute - dayStartMinute + MINUTES_PER_DAY) % MINUTES_PER_DAY;

/**
 * Build the configured Shift Time pivot bands.
 *
 * Every group is evaluated independently, so a row covering an hour of two groups prints in both
 * (a 7p–7a shift under Evening 3p–11p and Night 11p–7a). Configured groups are retained when empty;
 * Other is appended only when rows cover no group.
 */
export const buildCustomTimeGroupBands = (args: BuildCustomTimeGroupBandsArgs): CustomTimeGroupBand[] => {
	const { rows, groups, timezone, dayStart, sortDirection, otherLabel } = args;
	const dayStartMinute = parseDayStartMinute(dayStart);
	const indexedGroups = groups.map((group, index) => ({ group, index }));
	indexedGroups.sort((left, right) => {
		const difference = groupTimelineMinute(left.group, dayStartMinute) - groupTimelineMinute(right.group, dayStartMinute);
		if (difference === 0) return left.index - right.index;
		return sortDirection === "desc" ? -difference : difference;
	});

	const matchedRows = new Set<ScheduleRow>();
	const bands = indexedGroups.map(({ group }) => {
		const groupRows = rows.filter((row) => rowCoversGroup(row, group, timezone));
		for (const row of groupRows) matchedRows.add(row);
		const customName = group.name.trim();
		return {
			key: `${CONFIGURED_GROUP_KEY_PREFIX}${group.id}`,
			label: customName.length > 0 ? customName : formatCustomTimeGroupRange(group),
			rows: groupRows,
		};
	});

	const unmatchedRows = rows.filter((row) => !matchedRows.has(row));
	if (unmatchedRows.length > 0) bands.push({ key: UNMATCHED_GROUP_KEY, label: otherLabel, rows: unmatchedRows });
	return bands;
};

export const canAddCustomTimeGroup = (groups: CustomTimeGroup[]): boolean => groups.length < MAX_CUSTOM_TIME_GROUPS;

interface DuplicateCustomTimeGroupRangeArgs {
	groups: CustomTimeGroup[];
	groupId: string;
	startMinute: number;
	endMinute: number;
}

export const isDuplicateCustomTimeGroupRange = (args: DuplicateCustomTimeGroupRangeArgs): boolean =>
	args.groups.some((group) => group.id !== args.groupId && group.startMinute === args.startMinute && group.endMinute === args.endMinute);
